import React, {
  Component,
  createContext,
  useContext,
  useEffect,
  useId,
  useRef,
  useState,
  useSyncExternalStore,
  type ErrorInfo,
  type ReactNode,
} from 'react';
import { Button } from '../atoms/Button';

/*
 * React bindings for the framework-free chunk recovery controller that lives in
 * @mbbe/config. This package does not depend on @mbbe/config, so the contract is
 * described structurally; the controller and draft store created by an app are
 * assignable to it.
 */
export type ChunkRecoveryLanguage = 'tr' | 'en';
type DraftFieldsLike = { [key: string]: unknown };

export interface ChunkRecoveryItem {
  label: string;
  dirty: boolean;
  pending: boolean;
  canKeepDraft: boolean;
  lossNote?: string;
  /** The screen is gone; only its unsaved fields are kept so the person can still choose. */
  retained?: boolean;
}

export interface ChunkRecoveryStateLike {
  open: boolean;
  block: 'unsaved' | 'pending' | 'offline' | 'repeated' | 'storage' | 'failed' | 'clean' | null;
  items: readonly ChunkRecoveryItem[];
  hasDirty: boolean;
  hasPending: boolean;
  draftNotKept: boolean;
  reloadFailed: boolean;
}

export interface ChunkRecoveryWorkRegistration {
  label: string;
  dirty: boolean;
  pending: boolean;
  persist?: () => boolean;
  discard?: () => void;
  lossNote?: string;
}

export interface ChunkRecoveryControllerLike {
  getSnapshot(): ChunkRecoveryStateLike;
  subscribe(listener: () => void): () => void;
  register(registration: ChunkRecoveryWorkRegistration): {
    update(patch: Partial<ChunkRecoveryWorkRegistration>): void;
    release(): void;
  };
  reload(request: { keepDrafts: boolean }): 'reloading' | 'blocked-pending' | 'draft-not-kept' | 'failed';
  dismiss(): void;
  /** Keeps dirty work alive across the unmount a route boundary causes (see @mbbe/config). */
  retainWork?(): void;
}

export interface ChunkRecoveryDraftsLike {
  save(key: string, fields: Record<string, unknown>): string;
  take(key: string): DraftFieldsLike | null;
  discard(key: string): void;
}

interface RecoveryContextValue {
  controller: ChunkRecoveryControllerLike;
  drafts: ChunkRecoveryDraftsLike | null;
  language: ChunkRecoveryLanguage;
}

const RecoveryContext = createContext<RecoveryContextValue | null>(null);

const COPY = {
  tr: {
    updateTitle: 'Yeni sürüm hazır',
    updateBody: 'Uygulama güncellendi. Devam etmek için sayfayı yenileyin.',
    unsavedTitle: 'Kaydedilmemiş çalışmanız var',
    unsavedBody:
      'Uygulama güncellendi; sayfa kendiliğinden yenilenmedi. Yenilemeden önce ne yapılacağını siz seçin.',
    pendingTitle: 'Bir kayıt işlemi sürüyor',
    pendingBody:
      'İşlemin sonucu belli olmadan yenilemek sonucu gizleyebilir. İşlem tamamlanınca yenileyebilirsiniz; hiçbir işlem yenilemeden sonra otomatik tekrarlanmaz.',
    offlineTitle: 'Bağlantı yok',
    offlineBody: 'Yeni sürüm alınamıyor. Bağlantınız geri gelince sayfayı yenileyin.',
    repeatedTitle: 'Güncelleme yüklenemedi',
    repeatedBody:
      'Sayfa birkaç kez yenilendi ancak yeni sürüm hâlâ alınamıyor. Otomatik yenileme durduruldu; bağlantınızı kontrol edip elle yenileyin.',
    storageTitle: 'Otomatik yenileme kapalı',
    storageBody:
      'Tarayıcı depolaması kullanılamadığı için otomatik yenileme sınırlanamıyor. Hazır olduğunuzda sayfayı elle yenileyin.',
    failedTitle: 'Yenileme sorunu çözmedi',
    failedBody:
      'Yeni sürüm şu anda yüklenemiyor. Bağlantınızı kontrol edip biraz sonra yeniden deneyin.',
    reloadFailed: 'Sayfa yenilenemedi. Tarayıcının yenile düğmesini kullanın.',
    draftNotKept:
      'Bazı taslaklar saklanamadı. Taslağı atarak yenileyebilir veya çalışmaya devam edebilirsiniz.',
    keepsDraft: 'taslak korunabilir',
    losesDraft: 'kaydedilmemiş değişiklikler kaybolur',
    reopenToRestore: 'ekran kapandı; taslak, ekranı yeniden açtığınızda geri yüklenir',
    reopenLost: 'ekran kapandı; bu değişiklikler saklanamaz',
    reloadKeep: 'Taslağı koru ve yenile',
    reloadDiscard: 'Taslağı atıp yenile',
    reload: 'Sayfayı yenile',
    reloadWaiting: 'İşlem bitince yenile',
    keepWorking: 'Çalışmaya devam et',
    close: 'Kapat',
    restored: 'Yenilemeden önce saklanan taslağınız geri yüklendi.',
    boundaryBody: 'Yeni sürüm yayımlandı. Devam etmek için sayfayı yenileyin.',
    boundaryAction: 'Sayfayı yenile',
  },
  en: {
    updateTitle: 'A new version is ready',
    updateBody: 'The app was updated. Reload the page to continue.',
    unsavedTitle: 'You have unsaved work',
    unsavedBody: 'The app was updated and the page did not reload on its own. Choose what to do before reloading.',
    pendingTitle: 'A save is in progress',
    pendingBody:
      'Reloading before the result is known could hide the outcome. Reload once it finishes; nothing is resubmitted after a reload.',
    offlineTitle: 'You are offline',
    offlineBody: 'The new version cannot be fetched. Reload the page when your connection is back.',
    repeatedTitle: 'The update could not be loaded',
    repeatedBody:
      'The page reloaded several times but the new version is still unavailable. Automatic reloads stopped; check your connection and reload manually.',
    storageTitle: 'Automatic reload is off',
    storageBody:
      'Browser storage is unavailable, so automatic reloads cannot be limited. Reload the page yourself when you are ready.',
    failedTitle: 'Reloading did not fix it',
    failedBody: 'The new version cannot be loaded right now. Check your connection and try again shortly.',
    reloadFailed: 'The page could not be reloaded. Use your browser reload button.',
    draftNotKept: 'Some drafts could not be stored. You can discard them and reload, or keep working.',
    keepsDraft: 'draft can be kept',
    losesDraft: 'unsaved changes will be lost',
    reopenToRestore: 'the screen closed; the draft is restored when you reopen it',
    reopenLost: 'the screen closed; these changes cannot be kept',
    reloadKeep: 'Keep draft and reload',
    reloadDiscard: 'Discard draft and reload',
    reload: 'Reload page',
    reloadWaiting: 'Reload when finished',
    keepWorking: 'Keep working',
    close: 'Close',
    restored: 'Your draft saved before the reload was restored.',
    boundaryBody: 'A new version was published. Reload the page to continue.',
    boundaryAction: 'Reload page',
  },
} as const;

export function ChunkRecoveryProvider({
  controller,
  drafts = null,
  language = 'tr',
  children,
}: {
  controller: ChunkRecoveryControllerLike;
  drafts?: ChunkRecoveryDraftsLike | null;
  language?: ChunkRecoveryLanguage;
  children: ReactNode;
}) {
  const value = React.useMemo(() => ({ controller, drafts, language }), [controller, drafts, language]);
  return (
    <RecoveryContext.Provider value={value}>
      {children}
      <ChunkRecoveryPrompt />
    </RecoveryContext.Provider>
  );
}

function useRecoverySnapshot(controller: ChunkRecoveryControllerLike | undefined): ChunkRecoveryStateLike | null {
  const subscribe = React.useCallback(
    (listener: () => void) => (controller ? controller.subscribe(listener) : () => undefined),
    [controller],
  );
  const getSnapshot = React.useCallback(() => (controller ? controller.getSnapshot() : null), [controller]);
  return useSyncExternalStore(subscribe, getSnapshot, getSnapshot);
}

const TITLE_KEYS = {
  clean: ['updateTitle', 'updateBody'],
  unsaved: ['unsavedTitle', 'unsavedBody'],
  pending: ['pendingTitle', 'pendingBody'],
  offline: ['offlineTitle', 'offlineBody'],
  repeated: ['repeatedTitle', 'repeatedBody'],
  storage: ['storageTitle', 'storageBody'],
  failed: ['failedTitle', 'failedBody'],
} as const;

export function ChunkRecoveryPrompt() {
  const context = useContext(RecoveryContext);
  const snapshot = useRecoverySnapshot(context?.controller);
  const headingId = useId();
  const bodyId = useId();
  const dialogRef = useRef<HTMLElement>(null);
  const returnFocusRef = useRef<HTMLElement | null>(null);
  const isOpen = Boolean(context && snapshot?.open && snapshot.block);

  // The prompt is a modal alert dialog so assistive technology reaches it even while another
  // modal (a drawer or dialog) is open. Focus moves to the dialog itself, never to a button, so
  // a stray Enter or Space while typing cannot trigger a reload; it returns to the form on close.
  useEffect(() => {
    if (!isOpen) return undefined;
    returnFocusRef.current = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    dialogRef.current?.focus();
    return () => {
      const target = returnFocusRef.current;
      returnFocusRef.current = null;
      if (target && target.isConnected) target.focus();
    };
  }, [isOpen]);

  if (!context || !snapshot || !snapshot.open || !snapshot.block) return null;
  const copy = COPY[context.language];
  const [titleKey, bodyKey] = TITLE_KEYS[snapshot.block];
  const allKeepable = snapshot.items.filter((item) => item.dirty).every((item) => item.canKeepDraft);
  const waiting = snapshot.hasPending;

  const onKeyDown = (event: React.KeyboardEvent<HTMLElement>) => {
    // The prompt sits above other modal surfaces (a review drawer listening for Escape on window).
    // Keys handled here must not reach them, or "keep working" would also close the form behind.
    if (event.key === 'Escape') {
      event.preventDefault();
      event.stopPropagation();
      context.controller.dismiss();
      return;
    }
    if (event.key !== 'Tab') return;
    event.stopPropagation();
    const focusable = Array.from(
      dialogRef.current?.querySelectorAll<HTMLElement>('button:not([disabled])') ?? [],
    );
    if (!focusable.length) {
      event.preventDefault();
      return;
    }
    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    const active = document.activeElement;
    if (event.shiftKey && (active === first || active === dialogRef.current)) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && active === last) {
      event.preventDefault();
      first.focus();
    }
  };

  return (
    <div className="fixed inset-0 z-[10000] flex items-end justify-center bg-black/50 p-4">
      <section
        ref={dialogRef}
        role="alertdialog"
        aria-modal="true"
        aria-labelledby={headingId}
        aria-describedby={bodyId}
        tabIndex={-1}
        onKeyDown={onKeyDown}
        data-chunk-recovery={snapshot.block}
        className="w-full max-w-xl rounded-lg border border-slate-700 bg-slate-800 p-4 text-left shadow-xl focus:outline-none"
      >
        <h2 id={headingId} className="text-sm font-semibold text-white">
          {copy[titleKey]}
        </h2>
        <p id={bodyId} className="mt-1 text-sm text-slate-300">
          {copy[bodyKey]}
        </p>
        {snapshot.items.length > 0 && (
          <ul className="mt-3 space-y-1 text-xs text-slate-300">
            {snapshot.items.map((item, index) => (
              <li key={`${item.label}-${index}`}>
                <span className="font-medium text-white">{item.label}</span>
                {item.pending
                  ? ` - ${copy.pendingTitle.toLowerCase()}`
                  : ` - ${item.canKeepDraft ? copy.keepsDraft : copy.losesDraft}`}
                {item.retained ? `; ${item.canKeepDraft ? copy.reopenToRestore : copy.reopenLost}` : ''}
                {item.lossNote ? `; ${item.lossNote}` : ''}
              </li>
            ))}
          </ul>
        )}
        {snapshot.draftNotKept && (
          <p role="status" className="mt-3 text-xs text-amber-300">
            {copy.draftNotKept}
          </p>
        )}
        {snapshot.reloadFailed && (
          <p role="status" className="mt-3 text-xs text-red-300">
            {copy.reloadFailed}
          </p>
        )}
        <div className="mt-4 flex flex-wrap gap-2">
          {snapshot.hasDirty && !waiting ? (
            <>
              {allKeepable && !snapshot.draftNotKept && (
                <Button size="sm" onClick={() => context.controller.reload({ keepDrafts: true })}>
                  {copy.reloadKeep}
                </Button>
              )}
              <Button
                size="sm"
                variant="danger"
                onClick={() => context.controller.reload({ keepDrafts: false })}
              >
                {copy.reloadDiscard}
              </Button>
            </>
          ) : (
            <Button
              size="sm"
              disabled={waiting}
              onClick={() => context.controller.reload({ keepDrafts: true })}
            >
              {waiting ? copy.reloadWaiting : copy.reload}
            </Button>
          )}
          <Button size="sm" variant="outline" onClick={() => context.controller.dismiss()}>
            {snapshot.block === 'clean' || (snapshot.items.length > 0 && snapshot.items.every((item) => item.retained))
              ? copy.close
              : copy.keepWorking}
          </Button>
        </div>
      </section>
    </div>
  );
}

export interface UnsavedWorkOptions {
  /** Short human label shown in the recovery prompt. */
  label: string;
  /** The screen holds changes that are not saved on the server. */
  dirty: boolean;
  /** A mutation started by this screen has not settled yet. */
  pending?: boolean;
  /**
   * Entity-specific draft key (for example `document:<id>`). When set together with
   * `getDraft` the draft is stored only if the person chooses to keep it for the reload.
   */
  draftKey?: string | null;
  /** Approved nonsecret fields only: never passwords, tokens, invitation links or signed URLs. */
  getDraft?: () => Record<string, unknown>;
  /** Called once, on mount, with the draft stored before the last reload. */
  onRestore?: (fields: Record<string, unknown>) => void;
  /** The screen already stores its own draft continuously. */
  alreadyKept?: boolean;
  /**
   * Drops the screen's own stored draft. Called when the person chooses "discard draft and reload",
   * so a screen that keeps its own draft (`alreadyKept`) does not bring it back after the reload.
   */
  onDiscard?: () => void;
  /** Extra explicit loss, for example files that cannot be stored. */
  lossNote?: string;
}

/**
 * Registers a screen's unsaved/pending work with the chunk recovery controller.
 * Without a ChunkRecoveryProvider (for example in isolated component tests) it is a no-op.
 */
export function useUnsavedWork(options: UnsavedWorkOptions): { restored: boolean; dismissRestored: () => void } {
  const context = useContext(RecoveryContext);
  const [restored, setRestored] = useState(false);
  const latest = useRef(options);
  latest.current = options;
  const handleRef = useRef<ReturnType<ChunkRecoveryControllerLike['register']> | null>(null);
  const drafts = context?.drafts ?? null;
  const supportsDraft = Boolean(options.alreadyKept || (options.draftKey && options.getDraft && drafts));

  const persist = React.useCallback((): boolean => {
    const current = latest.current;
    if (current.alreadyKept) return true;
    if (!current.draftKey || !current.getDraft || !drafts) return false;
    return drafts.save(current.draftKey, current.getDraft()) === 'saved';
  }, [drafts]);

  const discard = React.useCallback((): void => {
    const current = latest.current;
    if (current.draftKey && drafts) drafts.discard(current.draftKey);
    current.onDiscard?.();
  }, [drafts]);

  const controller = context?.controller;
  useEffect(() => {
    if (!controller) return undefined;
    const current = latest.current;
    handleRef.current = controller.register({
      label: current.label,
      dirty: current.dirty,
      pending: Boolean(current.pending),
      persist: supportsDraft ? persist : undefined,
      discard,
      lossNote: current.lossNote,
    });
    return () => {
      handleRef.current?.release();
      handleRef.current = null;
    };
    // The registration is stable for the screen's lifetime; changes flow through update() below.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [controller]);

  useEffect(() => {
    handleRef.current?.update({
      label: options.label,
      dirty: options.dirty,
      pending: Boolean(options.pending),
      persist: supportsDraft ? persist : undefined,
      discard,
      lossNote: options.lossNote,
    });
  }, [options.label, options.dirty, options.pending, options.lossNote, supportsDraft, persist, discard]);

  const restoredKey = useRef<string | null>(null);
  useEffect(() => {
    const key = options.draftKey;
    if (!key || !drafts || !latest.current.onRestore) return;
    if (restoredKey.current === key) return;
    restoredKey.current = key;
    const fields = drafts.take(key);
    if (fields) {
      latest.current.onRestore(fields);
      setRestored(true);
    }
  }, [options.draftKey, drafts]);

  return { restored, dismissRestored: () => setRestored(false) };
}

/** Language of the recovery prompt, so a screen can word its label and loss note to match it. */
export function useChunkRecoveryLanguage(): ChunkRecoveryLanguage {
  return useContext(RecoveryContext)?.language ?? 'tr';
}

/** Text of the notice that tells the person a stored draft was restored. */
export function useChunkRecoveryCopy() {
  const context = useContext(RecoveryContext);
  return COPY[context?.language ?? 'tr'];
}

function isChunkLoadErrorLike(error: unknown): boolean {
  if (!(error instanceof Error)) return false;
  return (
    error.name === 'ChunkLoadError' ||
    error.message.includes('Failed to fetch dynamically imported module') ||
    error.message.includes('Importing a module script failed') ||
    error.message.includes('error loading dynamically imported module') ||
    error.message.includes('Unable to preload CSS')
  );
}

function ChunkLoadFallback() {
  const context = useContext(RecoveryContext);
  const snapshot = useRecoverySnapshot(context?.controller);
  const copy = COPY[context?.language ?? 'tr'];
  const headingRef = useRef<HTMLParagraphElement>(null);
  const [fallbackFailed, setFallbackFailed] = useState(false);
  useEffect(() => {
    // When the shared prompt is already open it owns focus: this fallback sits behind its modal overlay.
    if (context?.controller.getSnapshot().open) return;
    headingRef.current?.focus();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const reload = () => {
    if (context) {
      context.controller.reload({ keepDrafts: true });
      return;
    }
    try {
      window.location.reload();
    } catch {
      setFallbackFailed(true);
    }
  };
  const waiting = Boolean(snapshot?.hasPending);
  return (
    <main className="flex min-h-screen flex-col items-center justify-center gap-4 bg-dark-900 px-4 text-center">
      <p ref={headingRef} tabIndex={-1} className="max-w-md text-base text-slate-300 focus:outline-none">
        {copy.boundaryBody}
      </p>
      {/* With a provider the shared prompt reports blocked/failed recovery; this only covers the bare case. */}
      {fallbackFailed && (
        <p role="alert" className="max-w-md text-sm text-amber-300">
          {copy.reloadFailed}
        </p>
      )}
      <Button disabled={waiting} onClick={reload}>
        {waiting ? copy.reloadWaiting : copy.boundaryAction}
      </Button>
    </main>
  );
}

interface ChunkLoadBoundaryState {
  failed: boolean;
}

/**
 * Route-level lazy loading boundary. A missing chunk shows a reload action that goes
 * through the shared recovery controller (pending work blocks it, drafts are kept); every
 * other error is rethrown to the surrounding ErrorBoundary.
 */
export class ChunkLoadBoundary extends Component<{ children: ReactNode }, ChunkLoadBoundaryState> {
  static contextType = RecoveryContext;
  declare context: React.ContextType<typeof RecoveryContext>;

  state: ChunkLoadBoundaryState = { failed: false };

  static getDerivedStateFromError(error: unknown): ChunkLoadBoundaryState | null {
    return isChunkLoadErrorLike(error) ? { failed: true } : null;
  }

  componentDidCatch(error: Error, info: ErrorInfo): void {
    if (!isChunkLoadErrorLike(error)) return;
    console.error('Lazy route chunk failed to load:', error, info.componentStack);
    // This runs in the commit that replaces the screens below, before their effect cleanups, so the
    // unsaved work they registered can still be handed over to the controller.
    try {
      this.context?.controller.retainWork?.();
    } catch {
      // recovery must never crash the page that is already recovering
    }
  }

  render(): ReactNode {
    return this.state.failed ? <ChunkLoadFallback /> : this.props.children;
  }
}
